const EXCLUDED_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "SVG", "CANVAS"]);

export function normalizePagePath(pathname) {
  const path = String(pathname || "/").split("?", 1)[0] || "/";
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

function elementSegment(element) {
  if (element.id && !element.id.includes(":")) {
    const escapedId = window.CSS?.escape
      ? window.CSS.escape(element.id)
      : element.id.replace(/([^a-zA-Z0-9_-])/g, "\\$1");
    return `#${escapedId}`;
  }
  const tag = element.tagName.toLowerCase();
  const siblings = Array.from(element.parentElement?.children || []).filter(
    (item) => item.tagName === element.tagName,
  );
  return siblings.length > 1 ? `${tag}:nth-of-type(${siblings.indexOf(element) + 1})` : tag;
}

export function getElementLocator(element) {
  const segments = [];
  let current = element;
  while (current && current.id !== "root" && current !== document.body) {
    const segment = elementSegment(current);
    segments.unshift(segment);
    if (segment.startsWith("#")) {
      break;
    }
    current = current.parentElement;
  }
  const selector = segments.join(" > ");
  return segments[0]?.startsWith("#") ? selector : `#root > ${selector}`;
}

export function findTextOverrideTargets(override, items = collectTextItems()) {
  const matches = (item) =>
    item.sourceValue === override.source_value || item.sourceValue === override.replacement_value;
  const exact = items.find((item) => item.locator === override.locator && matches(item));
  if (exact) return [exact];
  // Old locators can point to a different section after a tab or layout change.
  return items.filter((item) => item.sourceValue === override.source_value);
}

export function getTextLocator(node) {
  const childIndex = Array.from(node.parentElement.childNodes).indexOf(node);
  return `${getElementLocator(node.parentElement)}::text(${childIndex})`;
}

export function resolveTextLocator(locator) {
  const match = String(locator || "").match(/^(.*)::text\((\d+)\)$/);
  if (!match) {
    return null;
  }
  try {
    const parent = document.querySelector(match[1]);
    const node = parent?.childNodes?.[Number(match[2])];
    return node?.nodeType === Node.TEXT_NODE ? node : null;
  } catch (_error) {
    return null;
  }
}

function isExcludedElement(element) {
  return (
    !element ||
    EXCLUDED_TAGS.has(element.tagName) ||
    element.closest("[data-content-editor-ignore='true']") ||
    element.closest(".maplibregl-map")
  );
}

export function collectTextItems(root = document.getElementById("root")) {
  if (!root) {
    return [];
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const items = [];
  let node = walker.nextNode();
  while (node) {
    const value = node.nodeValue?.trim();
    if (value && !isExcludedElement(node.parentElement)) {
      items.push({
        kind: "text",
        locator: getTextLocator(node),
        sourceValue: value,
        node,
        element: node.parentElement,
      });
    }
    node = walker.nextNode();
  }
  return items;
}

export function extractEditableImage(element) {
  if (isExcludedElement(element)) {
    return null;
  }
  let source = "";
  let imageType = "image";
  if (element.tagName === "IMG") {
    source = element.getAttribute("src") || "";
  } else {
    const background = element.style.backgroundImage || "";
    const match = background.match(/url\(["']?([^"')]+)["']?\)/);
    source = match?.[1] || "";
    imageType = "background";
  }
  if (!source || source.startsWith("blob:") || source.startsWith("data:") || source.includes("/api/uploads/")) {
    return null;
  }
  try {
    const parsed = new URL(source, window.location.origin);
    if (parsed.origin !== window.location.origin || !parsed.pathname.startsWith("/images/") && !parsed.pathname.startsWith("/api/content/assets/")) {
      return null;
    }
  } catch (_error) {
    return null;
  }
  return {
    kind: "image",
    imageType,
    locator: getElementLocator(element),
    sourceValue: source,
    element,
  };
}

export function collectImageItems(root = document.getElementById("root")) {
  if (!root) {
    return [];
  }
  return Array.from(root.querySelectorAll("img, [style*='background-image']"))
    .map(extractEditableImage)
    .filter(Boolean);
}

export function replaceTextNodeValue(node, source, replacement) {
  const current = node.nodeValue || "";
  if (current.trim() === replacement) {
    return;
  }
  const index = current.indexOf(source);
  node.nodeValue = index >= 0
    ? `${current.slice(0, index)}${replacement}${current.slice(index + source.length)}`
    : replacement;
}
